import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SETTING_CONTENTS_NAMES, ThemeType, FontType, ProtocolType, RenderingQualityKeyType } from "config/settingsConfig";
import { DicomNodeParams } from "@models/pacs";

export interface GeneralSetting {
  theme: ThemeType;
  font: FontType;
  cacheLimit: number;
  automaticallyClearCache: boolean;
  intervalDay: number;
  usageStorage: number;
  freeStorage: number;
  storageAlertNotification: boolean;
  GPUAcceleration: boolean;
  renderingQuality: RenderingQualityKeyType;
  enableProxyServer: boolean;
  proxyIP: string;
  proxyPort: string;
}

interface Node{
  AETitle: string;
  port : number;
  seriesNumberBase : Number;
}

export interface ClientNode extends Node{
  acceptReceivingStudies : boolean
}

export interface ServerNode extends Node{
  description : string;
  ip : string;
  protocol : ProtocolType;
}
const initialNode : Node= {
  AETitle:'',
  port:0,
  seriesNumberBase: 0,
};
export const initialClient : ClientNode= {
  ...initialNode,
  acceptReceivingStudies:false
};

const initialServerNode : ServerNode= {
  ...initialNode,
  description:'',
  ip:'',
  protocol:SETTING_CONTENTS_NAMES.pacsConnection.protocols.CMove
}

export interface PACSConnectionSetting {
  client: ClientNode,
  servers: ServerNode[],
  defaultServer : DicomNodeParams[]
}

export interface AccountSetting {

}

export interface SettingsData {
  general: GeneralSetting;
  pacsConnection: PACSConnectionSetting;
  account: AccountSetting;
}

interface SettingsState {
  data : SettingsData;
  loading:boolean;
  error : string | null;
}


const initialState: SettingsState = {
  data:{
    general: {
      theme: SETTING_CONTENTS_NAMES.general.themes.darkMode,
      font: SETTING_CONTENTS_NAMES.general.fonts.pretendard,
      cacheLimit: 1024,
      automaticallyClearCache: true,
      intervalDay: 1,
      usageStorage: 120,
      freeStorage: 80,
      storageAlertNotification: true,
      GPUAcceleration: true,
      renderingQuality: 'high',
      enableProxyServer: false,
      proxyIP: "",
      proxyPort: '',
    },
    pacsConnection: {
      client: initialClient,
      servers:[],
      defaultServer:[]
    },
    account:{

    },
  },
  loading:false,
  error:null
};


export const fetchSettings = createAsyncThunk(
  'settings/fetchSettings',
  async (_, thunkAPI) => {
    // 서버 API 호출 (예: /api/settings)
    const response = await fetch('/api/settings');
    if (!response.ok) {
      throw new Error('Failed to fetch settings');
    }
    return (await response.json()) as SettingsData;
  }
);

const settingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    updateGeneralSetting: (state, action: PayloadAction<Partial<GeneralSetting>>) => {
        state.data.general = { ...state.data.general, ...action.payload };
    },
    updatePACSConnectionSetting: (state, action: PayloadAction<Partial<PACSConnectionSetting>>) => {
        state.data.pacsConnection = { ...state.data.pacsConnection, ...action.payload };
    },
    updateAccountSetting: (state, action: PayloadAction<Partial<PACSConnectionSetting>>) => {
        state.data.account = { ...state.data.account, ...action.payload };
    },
    setTheme(state,action:PayloadAction<ThemeType>){
      state.data.general.theme = action.payload;
    },
    setFont(state,action:PayloadAction<FontType>){
      state.data.general.font = action.payload;
    },
    setCacheLimit(state,action:PayloadAction<number>){
      state.data.general.cacheLimit = action.payload;
    },
    setAutomaticallyClearCacheFlag(state,action:PayloadAction<boolean>){
      state.data.general.automaticallyClearCache = action.payload;
    },
    setIntervalDay(state,action:PayloadAction<number>){
      state.data.general.intervalDay = action.payload;
    },
    setGPUAccelerationFlag(state,action:PayloadAction<boolean>){
      state.data.general.GPUAcceleration = action.payload;
    },
    setStorageAlertNotificationFlag(state,action:PayloadAction<boolean>){
      state.data.general.storageAlertNotification = action.payload;
    },
    setRenderingQualityType(state,action:PayloadAction<RenderingQualityKeyType>){
      state.data.general.renderingQuality = action.payload;
    },
    setEnableProxyServerFlag(state,action:PayloadAction<boolean>){
      state.data.general.enableProxyServer = action.payload;
    },
    setProxyIP(state,action:PayloadAction<string>){
      state.data.general.proxyIP = action.payload;
    },
    setProxyPort(state,action:PayloadAction<string>){
      state.data.general.proxyPort = action.payload;
    },
    setAETitle(state, action:PayloadAction<string>){
      state.data.pacsConnection.client.AETitle = action.payload;
    },
    setPort(state, action:PayloadAction<number>){
      state.data.pacsConnection.client.port = action.payload;
    },
    setSeriesNumberBase(state, action:PayloadAction<number>){
      state.data.pacsConnection.client.seriesNumberBase = action.payload;
    },
    setacceptReceivingStudies(state, action:PayloadAction<boolean>){
      state.data.pacsConnection.client.acceptReceivingStudies = action.payload;
    },
    setServerNode(state, action:PayloadAction<ServerNode>){
      state.data.pacsConnection.servers.push(action.payload);
    },
    setDefaultServer(state, action:PayloadAction<DicomNodeParams[]>){
      state.data.pacsConnection.defaultServer = action.payload;
    }
  },
  extraReducers:(builder)=>{
    builder
    .addCase(fetchSettings.pending, (state)=>{
      state.loading=true;
      state.error=null;
    })
    .addCase(fetchSettings.fulfilled, (state, action : PayloadAction<SettingsData>)=>{
      state.loading=false;
      state.data = action.payload;
    })
    .addCase(fetchSettings.rejected, (state,action)=>{
      state.loading=false;
      state.error=action.error.message || 'Failed to fetch settings';
    })
  }
});

export const { updateGeneralSetting, updatePACSConnectionSetting, updateAccountSetting,
  setTheme,
  setFont,
  setCacheLimit,
  setAutomaticallyClearCacheFlag,
  setIntervalDay,
  setGPUAccelerationFlag,
  setStorageAlertNotificationFlag,
  setRenderingQualityType,
  setEnableProxyServerFlag,
  setProxyIP,
  setProxyPort,
  setAETitle,
  setPort,
  setSeriesNumberBase,
  setacceptReceivingStudies,
  setServerNode,
  setDefaultServer
} = settingsSlice.actions;
export default settingsSlice.reducer;
